import type { Deployment } from '../services/api'

/** A deployment's status on the list, worst first in DEPLOYMENT_STATUS_ORDER. */
export type DeploymentStatus =
  | 'failed'
  | 'not_in_intune'
  | 'out_of_sync'
  | 'pending'
  | 'not_assigned'
  | 'unknown'
  | 'assigned'

export const DEPLOYMENT_STATUS_ORDER: DeploymentStatus[] = [
  'failed', 'not_in_intune', 'out_of_sync', 'pending', 'not_assigned', 'unknown', 'assigned',
]

/** One deployment as the list shows it: the install app and the update-only app of one package
 *  deployment, in one row. */
export interface DeploymentRow {
  key: string
  /** The app the row stands for: the install app, or the update-only app when there is none. Its
   *  id opens the deployment page and removes the deployment. */
  primary: Deployment
  install?: Deployment
  update?: Deployment
  /** Every app object of the deployment, with older versions a removal could not delete. */
  apps: Deployment[]
  /** Apps of failed publishes left in Intune beside the deployment's published apps. */
  leftovers: Deployment[]
  status: DeploymentStatus
  /** The install app is in Intune but the update-only app was deleted there. The row's status is
   *  then the install app's, with a note naming the missing app. */
  updateMissing: boolean
  lastUpdated: string | null
}

/** The status of one app object. */
export function appStatus(d: Deployment): DeploymentStatus {
  if (d.status === 'failed') return 'failed'
  if (d.in_intune === false) return 'not_in_intune'
  if (d.out_of_sync) return 'out_of_sync'
  if (d.status === 'pending') return 'pending'
  if (d.assigned === false) return 'not_assigned'
  if (d.assigned === true) return 'assigned'
  return 'unknown'
}

function worst(statuses: DeploymentStatus[]): DeploymentStatus {
  return statuses.reduce<DeploymentStatus>(
    (a, b) => (DEPLOYMENT_STATUS_ORDER.indexOf(b) < DEPLOYMENT_STATUS_ORDER.indexOf(a) ? b : a),
    'assigned',
  )
}

/** How a deployment is named wherever it is one item: the package, then the deployment's name.
 *  The package's first deployment has no name; it is the Default one. */
export function deploymentTitle(packageName: string, deploymentName: string | null | undefined): string {
  return `${packageName} · ${deploymentName?.trim() || 'Default'}`
}

/** The list's rows grouped by deployment, in the order each deployment first appears. A row with
 *  no `deployment_key` (an app no record names) is a deployment of its own. */
export function groupDeploymentRows(rows: Deployment[]): DeploymentRow[] {
  const groups = new Map<string, Deployment[]>()
  for (const d of rows) {
    const key = d.deployment_key ?? `app:${d.id}`
    const list = groups.get(key)
    if (list) list.push(d)
    else groups.set(key, [d])
  }
  return [...groups.entries()].map(([key, apps]) => {
    const current = apps.filter((d) => !d.removal_incomplete)
    // A published app stands for its role; a failed publish's app only when there is no other.
    const pick = (isUpdate: boolean) => {
      const ofRole = current.filter((d) => (d.role === 'update_only') === isUpdate)
      return ofRole.find((d) => !d.failed_publish) ?? ofRole[0]
    }
    const install = pick(false)
    const update = pick(true)
    const primary = install ?? update ?? apps[0]
    const shown = [install, update].filter((d): d is Deployment => !!d)
    // A deleted update-only app does not make the whole deployment "Not in Intune" while the
    // install app is still there: the row says which app is missing instead.
    const updateMissing = !!install && install.in_intune !== false && update?.in_intune === false
    const counted = updateMissing ? [install] : shown
    const stamps = apps.map((d) => d.last_updated).filter((s): s is string => !!s).sort()
    return {
      key,
      primary,
      install,
      update,
      apps,
      leftovers: apps.filter((d) => d.failed_publish && d !== install && d !== update),
      status: worst((counted.length ? counted : apps).map(appStatus)),
      updateMissing,
      lastUpdated: stamps.length ? stamps[stamps.length - 1] : null,
    }
  })
}
